import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase.js';
import { AI_ADMIN_QUERY_KEYS } from './queries.js';
import type { TestProviderResponse } from './types.js';

/** Parâmetros do upsert de provider (id opcional = criar). */
export interface UpsertProviderInput {
  id: string | null;
  displayName: string;
  baseUrl: string;
  modelId: string;
  /** API key em claro. Se `null`, mantém chave atual (edição). */
  apiKey: string | null;
}

export interface UpsertProviderResult {
  providerId: string;
}

/**
 * Cria ou atualiza um provider + seu modelo principal via RPC
 * `admin_upsert_provider`. Em seguida (se `apiKey` preenchida) rotaciona a
 * chave via `admin_set_provider_key`. Invalida cache de providers no sucesso.
 */
export function useUpsertProvider(): UseMutationResult<
  UpsertProviderResult,
  Error,
  UpsertProviderInput
> {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input): Promise<UpsertProviderResult> => {
      const { data: upsertedId, error: upsertError } = await supabase.rpc(
        'admin_upsert_provider' as never,
        {
          p_id: input.id,
          p_display_name: input.displayName,
          p_base_url: input.baseUrl,
          p_model_id: input.modelId,
        } as never,
      );

      if (upsertError) throw new Error(upsertError.message);
      const providerId = upsertedId as unknown as string;
      if (!providerId) throw new Error('upsert failed: no id returned');

      if (input.apiKey && input.apiKey.trim().length > 0) {
        const { error: keyError } = await supabase.rpc(
          'admin_set_provider_key' as never,
          {
            p_provider_id: providerId,
            p_plain_key: input.apiKey.trim(),
          } as never,
        );
        if (keyError) throw new Error(keyError.message);
      }

      return { providerId };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AI_ADMIN_QUERY_KEYS.providers });
      void qc.invalidateQueries({ queryKey: AI_ADMIN_QUERY_KEYS.models });
    },
  });
}

/**
 * Rotaciona a API key de um provider já existente, sem editar os outros
 * campos. Usado pelo fluxo "Alterar chave" na UI.
 */
export function useSetProviderKey(): UseMutationResult<
  void,
  Error,
  { providerId: string; apiKey: string }
> {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ providerId, apiKey }) => {
      const { error } = await supabase.rpc(
        'admin_set_provider_key' as never,
        {
          p_provider_id: providerId,
          p_plain_key: apiKey.trim(),
        } as never,
      );
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AI_ADMIN_QUERY_KEYS.providers });
    },
  });
}

/**
 * Exclui um provider via RPC `admin_delete_provider`. Postgres bloqueia se
 * algum módulo ainda referencia modelo dele; a UI precisa traduzir o erro.
 */
export function useDeleteProvider(): UseMutationResult<void, Error, { providerId: string }> {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ providerId }) => {
      const { error } = await supabase.rpc(
        'admin_delete_provider' as never,
        {
          p_id: providerId,
        } as never,
      );
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AI_ADMIN_QUERY_KEYS.providers });
      void qc.invalidateQueries({ queryKey: AI_ADMIN_QUERY_KEYS.models });
    },
  });
}

/**
 * Atribui um modelo ao módulo (ai_module_configs.default_model_id).
 * RPC valida que o modelo está habilitado.
 */
export function useAssignModuleModel(): UseMutationResult<
  void,
  Error,
  { moduleKey: string; modelId: string }
> {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ moduleKey, modelId }) => {
      const { error } = await supabase.rpc(
        'admin_assign_module_model' as never,
        {
          p_module_key: moduleKey,
          p_model_id: modelId,
        } as never,
      );
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AI_ADMIN_QUERY_KEYS.moduleConfigs });
    },
  });
}

/**
 * Atribui (ou limpa) o modelo de fallback do módulo
 * (ai_module_configs.fallback_model_id). Passe `modelId = null` para zerar.
 * RPC valida que o modelo está habilitado quando não-null. Ver migration
 * 0051 pra motivação (bug 2026-04-24: fallbacks só eram editáveis via SQL).
 */
export function useAssignModuleFallback(): UseMutationResult<
  void,
  Error,
  { moduleKey: string; modelId: string | null }
> {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ moduleKey, modelId }) => {
      const { error } = await supabase.rpc(
        'admin_assign_module_fallback' as never,
        {
          p_module_key: moduleKey,
          p_model_id: modelId,
        } as never,
      );
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AI_ADMIN_QUERY_KEYS.moduleConfigs });
    },
  });
}

/**
 * Invoca a Edge Function `ai-gateway-test`, que faz um ping real ao provider
 * (usando a chave cifrada em ai_providers) e devolve latência + status +
 * amostra de resposta. Não invalida cache.
 */
export function useTestProvider(): UseMutationResult<
  TestProviderResponse,
  Error,
  { providerId: string; modelId?: string }
> {
  return useMutation({
    mutationFn: async ({ providerId, modelId }): Promise<TestProviderResponse> => {
      const body: { providerId: string; modelId?: string } = { providerId };
      if (modelId) body.modelId = modelId;
      const { data, error } = await supabase.functions.invoke<TestProviderResponse>(
        'ai-gateway-test',
        { body },
      );
      if (error) throw new Error(error.message);
      if (!data) throw new Error('empty response');
      return data;
    },
  });
}
